import { z } from "zod";

import { extractCustom, getCustomFieldsForSubmit } from "components/helpers";
import {
  CUSTOM_FIELD_ERROR_MESSAGES,
  TABLE_FORMAT_REGEX,
} from "constants/CustomFields";
import { mapCustomFieldValues } from "helpers/customFields";

/** The value an unfilled field holds; submitting it removes a stored value. */
export const emptyCustomFieldValue = config => {
  switch (config.field_type) {
    case "boolean":
      return false;
    case "list":
      return [];
    case "select":
      return config.field_options?.type === "multi" ? [] : null;
    case "date":
      return null;
    default:
      return "";
  }
};

const customFieldDefault = config =>
  config.value ?? emptyCustomFieldValue(config);

/** Form defaults for `custom_fields`, keyed by config label. */
export const buildCustomFieldDefaults = (configs, customFields) =>
  Object.fromEntries(
    configs.map(config => [
      config.label,
      customFields?.[config.label] ?? customFieldDefault(config),
    ])
  );

/**
 * The display-only config list behind CustomFieldsSection.
 *
 * On edit, a config already carrying a value is revealed; on create only the
 * required ones are. Pass null for `customFields` to get the create behaviour —
 * an existing record with no custom fields still takes the edit path.
 */
export const buildCustomFieldSelection = (configs, customFields) => {
  const cloned = configs.map(c => ({ ...c }));
  if (customFields == null) {
    return cloned.map(c => ({ ...c, selected: !!c.required }));
  }
  return mapCustomFieldValues(extractCustom(customFields), cloned);
};

export const customFieldsSchema = z.record(z.any());

/** The custom-field half of a form's `superRefine`. */
export const addCustomFieldIssues = (configs, customFields, ctx) => {
  configs.forEach(config => {
    const value = customFields[config.label];
    if (config.field_type === "table" && value && value.trim()) {
      if (!TABLE_FORMAT_REGEX.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["custom_fields", config.label],
          message: CUSTOM_FIELD_ERROR_MESSAGES.TABLE,
        });
      }
    }
    if (!config.required || config.field_type === "boolean") return;
    const empty = Array.isArray(value)
      ? value.length === 0
      : value == null || value === "";
    if (empty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["custom_fields", config.label],
        message: `${config.label} is required`,
      });
    }
  });
};

/** The `custom_fields` record as the API's key/value list. */
export const customFieldsPayload = (configs, customFields) =>
  getCustomFieldsForSubmit(
    configs,
    Object.entries(customFields).map(([key, value]) => ({ key, value }))
  );
